import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { OrderCancelRequestDto } from '@tiendanube/common';
import { Reason } from '@tiendanube/common/src/enums';

interface UseOrderCancelProps {
  defaultValues?: Partial<OrderCancelRequestDto>;
  isOpen?: boolean;
}

interface UseOrderCancelReturn {
  cancelOrderState: OrderCancelRequestDto;
  setCancelOrderState: React.Dispatch<
    React.SetStateAction<OrderCancelRequestDto>
  >;
  handleSelectChange: ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) => void;
  handleCheckboxChange: ({
    name,
    checked,
  }: {
    name: string;
    checked: boolean;
  }) => void;
  handleCustomReasonChange: ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) => void;
  isValidToCancel: boolean;
  resetForm: () => void;
  selectRef: React.RefObject<HTMLDivElement>;
  textareaRef: React.RefObject<HTMLDivElement>;
}

export const DEFAULT_CANCEL_VALUES: OrderCancelRequestDto = {
  reason: Reason.CUSTOMER,
  email: true,
  restock: true,
  refund: false,
  customReason: '',
};

export const CUSTOM_REASON_MAX_LENGTH = 75;

/**
 * Hook for managing order cancellation form state and validation
 * @param defaultValues - Optional default values for the form
 * @returns Form state, handlers, and validation functions
 */
const useOrderCancel = ({
  defaultValues = {},
  isOpen = false,
}: UseOrderCancelProps = {}): UseOrderCancelReturn => {
  const [cancelOrderState, setCancelOrderState] =
    useState<OrderCancelRequestDto>({
      ...DEFAULT_CANCEL_VALUES,
      ...defaultValues,
    });

  const selectRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLDivElement>(null);

  const handleSelectChange = useCallback(
    ({ name, value }: { name: string; value: string }) => {
      setCancelOrderState((state) => ({
        ...state,
        [name]: value,
      }));
    },
    [],
  );

  const handleCheckboxChange = useCallback(
    ({ name, checked }: { name: string; checked: boolean }) => {
      setCancelOrderState((state) => ({
        ...state,
        [name]: checked,
      }));
    },
    [],
  );

  const handleCustomReasonChange = useCallback(
    ({ name, value }: { name: string; value: string }) => {
      const limitedValue =
        value.length > CUSTOM_REASON_MAX_LENGTH
          ? value.substring(0, CUSTOM_REASON_MAX_LENGTH)
          : value;
      setCancelOrderState((state) => ({
        ...state,
        [name]: limitedValue,
      }));
    },
    [],
  );

  const isValidToCancel = useMemo(() => {
    if (!cancelOrderState.reason) {
      return false;
    }

    if (cancelOrderState.reason === Reason.OTHER) {
      return !(
        cancelOrderState.customReason &&
        cancelOrderState.customReason.trim().length === 0 &&
        cancelOrderState.customReason.length > 0
      );
    }

    return true;
  }, [cancelOrderState.reason, cancelOrderState.customReason]);

  const resetForm = useCallback(() => {
    setCancelOrderState({
      ...DEFAULT_CANCEL_VALUES,
      ...defaultValues,
    });
  }, [defaultValues]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (cancelOrderState.reason === Reason.OTHER) {
          const textareaElement =
            textareaRef.current?.querySelector('textarea');
          textareaElement?.focus();
        } else {
          const selectElement = selectRef.current?.querySelector('select');
          selectElement?.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, cancelOrderState.reason]);

  return {
    cancelOrderState,
    setCancelOrderState,
    handleSelectChange,
    handleCheckboxChange,
    handleCustomReasonChange,
    isValidToCancel,
    resetForm,
    selectRef,
    textareaRef,
  };
};

export default useOrderCancel;
